import { Formik, FieldArray } from 'formik';
import react, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  Form,
  Input,
  Checkbox,
  Button,
  Row,
  Col,
  Space,
  Modal,
  Select,
  Tooltip,
  ColorPicker,
  Switch,
  Cascader,
  Badge,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { ChevronDown, Edit3, ListChecks, Package, Trash2 } from 'lucide-react';
import {
  add_actionlist_data,
  get_machine_category_with_groups,
} from '../../../utils/api';
import { get_common_sub_categories_by_machine_group } from './mbmr_api';
import useAppSetting from '@/hooks/useAppSetting';
const fieldTypes = [
  { value: 'text', label: 'Text Input', dataType: 'string' },
  { value: 'number', label: 'Number Input', dataType: 'number' },
  { value: 'dropdown', label: 'Dropdown', dataType: 'string' },
  { value: 'radio', label: 'Radio Button', dataType: 'string' },
  { value: 'date', label: 'Date Picker', dataType: 'string' },
  { value: 'time', label: 'Time Picker', dataType: 'string' },
  { value: 'checkbox', label: 'Checkbox', dataType: 'boolean' },
  { value: 'button', label: 'Button', dataType: 'string' },
  { value: 'notification', label: 'Notification', dataType: 'string' },
];

const emptyField = {
  type: 'text',
  name: null,
  additional_fields: {
    required: false,
    options: [],
    buttons: [],
  },
};

// ── Notification configuration options ──────────────────────────────────────
// What the notification watches (which sub-operation, or all).
const NOTIFY_TARGETS = [
  { value: 'all', label: 'All Sub-operations' },
  { value: 'ingredients', label: 'Ingredients' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'product', label: 'Product' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'sampling', label: 'Sampling' },
  { value: 'line_clearance', label: 'Line Clearance' },
  { value: 'instruction', label: 'Instruction' },
  { value: 'action_list', label: 'Action List' },
];

// When the notification fires. Some criteria need extra inputs (duration / range).
const NOTIFY_CRITERIA = [
  { value: 'duration_exceeded', label: 'Duration Exceeded', needs: 'duration' },
  { value: 'not_started_within', label: 'Not Started Within', needs: 'duration' },
  { value: 'pending_after', label: 'Still Pending After', needs: 'duration' },
  { value: 'deviation', label: 'On Deviation', needs: null },
  { value: 'value_out_of_range', label: 'Value Out of Range', needs: null },
  { value: 'step_completed', label: 'On Step Completed', needs: null },
  { value: 'step_failed', label: 'On Step Failed', needs: null },
];

// What to do when it fires.
const NOTIFY_ACTIONS = [
  { value: 'notify', label: 'Notify' },
  { value: 'log_only', label: 'Log Only' },
];

const NOTIFY_SEVERITY = [
  { value: 'info',         label: 'Info' },
  { value: 'notification', label: 'Notification' },
  { value: 'alert',        label: 'Alert' },
];

const NOTIFY_CHANNELS = [
  { value: 'in_app', label: 'In-App' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const DURATION_UNITS = [
  { value: 'sec', label: 'Seconds' },
  { value: 'min', label: 'Minutes' },
  { value: 'hrs', label: 'Hours' },
];

// A criterion needs a duration input only for time-based triggers.
const criteriaNeedsDuration = (criteria) =>
  NOTIFY_CRITERIA.find((c) => c.value === criteria)?.needs === 'duration';

// ── Condition Block — what triggers this notification ────────────────────────
const CONDITION_SOURCES = [
  { value: 'none', label: 'Notification Only' },
  { value: 'duration', label: 'Duration' },
  { value: 'ingredient', label: 'Ingredient Quantity' },
  { value: 'monitoring', label: 'Monitoring Parameter' },
  { value: 'sampling', label: 'Sampling Result' },
  { value: 'prior_action', label: 'Prior Action Response' },
];

const NOTIFICATION_TIMING = [
  { value: 'at_start', label: 'At Operation Start' },
  { value: 'after_duration', label: 'After Duration' },
  { value: 'at_end', label: 'At Operation End' },
];

const DURATION_BASIS = [
  { value: 'absolute', label: 'Absolute Time' },
  { value: 'percent', label: '% Elapsed' },
];

const DURATION_OPERATORS = [
  { value: 'exceeded', label: 'Exceeded' },
  { value: 'not_started', label: 'Not Started Within' },
  { value: 'pending', label: 'Still Pending After' },
];

const THRESHOLD_OPERATORS = [
  { value: '>', label: '> Greater Than' },
  { value: '<', label: '< Less Than' },
  { value: '>=', label: '>= Greater or Equal' },
  { value: '<=', label: '<= Less or Equal' },
  { value: '=', label: '= Equals' },
  { value: 'between', label: 'Between' },
];

// Predefined notify roles for quick selection
const NOTIFY_ROLES = [
  { value: 'operator', label: 'Current Operator' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'qa_manager', label: 'QA Manager' },
  { value: 'custom', label: 'Custom Role…' },
];

// ── Notification schema ───────────────────────────────────────────────────────
// Fields common to every source type
const NOTIF_BASE = {
  name:             '',
  condition_source: 'none',
  timing:           'at_start',
  target:           'all',
  severity:              'notification',
  notify_role_ids:       [],
  channels:              ['in_app'],
  message:               '',
  message_is_html:       false,
  response_fields:       [],
};

// Fields that only exist for a specific condition_source.
const NOTIF_SOURCE_FIELDS = {
  none: {
    timing_duration_value: null,
    timing_duration_unit:  'min',
  },
  duration: {
    duration_basis:    'absolute',
    duration_operator: 'exceeded',
    duration_value:    null,
    duration_unit:     'min',
  },
  ingredient: {
    ingredient_code:    null,
    ingredient_unit:    '',
    threshold_basis:    'absolute',
    threshold_operator: '>',
    threshold_value:    null,
    threshold_value_max: null,
  },
  monitoring: {
    monitoring_parameter_code: null,
    monitoring_unit:           '',
    threshold_basis:           'absolute',
    threshold_operator:        '>',
    threshold_value:           null,
    threshold_value_max:       null,
  },
  sampling: {
    sampling_expected: 'pass',
  },
  prior_action: {
    prior_action_step_id:  null,
    prior_action_expected: 'yes',
  },
};

// All possible fields merged — used as Formik initial value so every field
// is controlled even before the user picks a source.
const emptyNotification = Object.values(NOTIF_SOURCE_FIELDS).reduce(
  (acc, fields) => ({ ...acc, ...fields }),
  { ...NOTIF_BASE },
);


const validationSchema = Yup.object().shape({
  action_id: Yup.string().nullable().required('Action is required'),
  fields: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().nullable().required('Type is required'),
        // name: Yup.string().nullable().required('Name is required'),
        additional_fields: Yup.object().shape({
          required: Yup.boolean(),
          options: Yup.array().of(
            Yup.object().shape({
              label: Yup.string().nullable().required('Option label is required'),
              value: Yup.string().nullable().required('Option value is required'),
            })
          ),
          buttons: Yup.array().of(
            Yup.object().shape({
              label: Yup.string().nullable().required('Button label is required'),
              color: Yup.string().nullable().required('Color is required'),
              hidden: Yup.boolean(),
              auto: Yup.boolean(),
              tags: Yup.array()
                .of(
                  Yup.object().shape({
                    tag_name: Yup.string().nullable().required('Tag name is required'),
                    data_type: Yup.string().nullable().required('Data type is required'),
                    value: Yup.string()
                      .nullable()
                      .when('read_write', {
                        is: 'write',
                        then: (schema) => schema.required('Value is required for write mode'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                      }),
                    read_write: Yup.string().nullable().required('Read/Write is required'),
                  })
                )
                .min(1, 'At least one tag is required'),
            })
          ),
        }),
      })
    )
    .min(1, 'At least one field is required'),
});

const ActionList = ({
  editIndex,
  onSubmit = (values) => console.log('Submitted values:', values),
  actions_field_list,
  parameter_list,
  configured_machines = [], // machine IDs configured in Machine Setup for this operation
  machine_setup_entries = [], // full machine setup entries (category/group/machine_ids)
  lockedRuleFields, // rule defaults that flow in from ActionListRules (display-only here)
  process_id,
  operation_id = null,         // null = new unsaved operation; set = existing saved operation
  operation_ingredients = [],  // this operation's ingredient entries [{ process_ingredient_id, ingredient, unit }]
  operation_monitoring = [],   // this operation's monitoring entries [{ process_parameter_id, parameter, unit }]
  operation_action_steps = [], // other action steps in this operation for Prior Action condition
  operation_max_duration = null, // "HH:mm" string — used to auto-calc % of elapsed duration
  roles = [],                  // roles from API: [{ role_id, role }]
  values = {
    action_id: null,
    fields: [{ ...emptyField }],
    notifications: [],
    execution_rules: null,
  },
}) => {
  // Parse "HH:mm" → total minutes (returns null if invalid)
  const maxDurMins = (() => {
    if (!operation_max_duration) return null;
    const parts = String(operation_max_duration).split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  })();

  const rulesEnabled = useAppSetting('Rules');
  // When enabled, the action-list machine cascader is restricted to the machines
  // configured in Machine Setup and defaults to the first configured machine's group.
  const defaultMachineSelection = useAppSetting('Action List Default Machine');

  // Machine IDs configured in Machine Setup (array prop wins, else flatten setup entries).
  const setupMachineIds = (Array.isArray(configured_machines) && configured_machines.length
    ? configured_machines
    : (machine_setup_entries || []).flatMap((m) =>
        Array.isArray(m.machine_ids) && m.machine_ids.length
          ? m.machine_ids
          : (m.machine_id ? [m.machine_id] : [])
      )
  ).map(Number).filter(Boolean);
  const setupMachineIdSet = new Set(setupMachineIds);
  // Default category/group from the first machine setup entry.
  const defaultSetup = (machine_setup_entries || [])[0] || {};
  const defaultMachineTypeId = defaultSetup.machine_type_id || null;
  const defaultMachineGroupId = defaultSetup.machine_group_id || null;
  // Rule defaults for Runtime Execution Rules (used to resolve the separate JSON on submit)
  const EXECUTION_RULE_DEFAULTS = {
    mandatory_step: true,
    block_next_step_until_complete: true,
    photo_capture_required: false,
    requires_supervisor_verification: false,
    requires_e_sign: false,
    machine_status_validation: false,
    auto_completion_from_plc: false,
  };
  // Resolve each rule: explicit per-item choice wins, else rule default (lockedRuleFields), else built-in default
  const resolveExecutionRules = (vals) => {
    const ruleBool = (key, fallback) =>
      lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
        ? !!lockedRuleFields[key]
        : fallback;
    return Object.keys(EXECUTION_RULE_DEFAULTS).reduce((acc, key) => {
      const chosen = (vals.execution_rules || {})[key];
      acc[key] =
        chosen !== undefined && chosen !== null ? !!chosen : ruleBool(key, EXECUTION_RULE_DEFAULTS[key]);
      return acc;
    }, {});
  };
  // Strip notification object to only the fields relevant to the chosen source.
  // Prevents irrelevant keys (e.g. sampling_expected when source=duration) from
  // being persisted to additional_field, which then confuses register_batch_notifications.
  const cleanNotification = (notif) => {
    if (!notif) return notif;
    const src = notif.condition_source || 'none';
    const base = {
      name:             notif.name ?? '',
      condition_source: src,
      timing:           notif.timing || 'at_start',
      target:           notif.target || 'all',
      severity:         notif.severity || 'notification',
      notify_role_ids:  notif.notify_role_ids || [],
      channels:         notif.channels || ['in_app'],
      message:          notif.message || '',
      message_is_html:  notif.message_is_html || false,
      response_fields:  notif.response_fields || [],
    };
    if (src === 'duration') return { ...base,
      duration_basis:    notif.duration_basis || 'absolute',
      duration_operator: notif.duration_operator || 'exceeded',
      duration_value:    notif.duration_value ?? null,
      duration_unit:     notif.duration_unit || 'min',
    };
    if (src === 'ingredient') return { ...base,
      ingredient_code:     notif.ingredient_code ?? null,
      ingredient_unit:     notif.ingredient_unit || '',
      threshold_basis:     notif.threshold_basis || 'absolute',
      threshold_operator:  notif.threshold_operator || '>',
      threshold_value:     notif.threshold_value ?? null,
      threshold_value_max: notif.threshold_value_max ?? null,
    };
    if (src === 'monitoring') return { ...base,
      monitoring_parameter_code: notif.monitoring_parameter_code ?? null,
      monitoring_unit:           notif.monitoring_unit || '',
      threshold_basis:           notif.threshold_basis || 'absolute',
      threshold_operator:        notif.threshold_operator || '>',
      threshold_value:           notif.threshold_value ?? null,
      threshold_value_max:       notif.threshold_value_max ?? null,
    };
    if (src === 'sampling') return { ...base,
      sampling_expected: notif.sampling_expected || 'pass',
    };
    if (src === 'prior_action') return { ...base,
      prior_action_step_id: notif.prior_action_step_id ?? null,
      prior_action_expected: notif.prior_action_expected || 'yes',
    };
    return { ...base,
      timing_duration_value: notif.timing_duration_value ?? null,
      timing_duration_unit:  notif.timing_duration_unit || 'min',
    };
  };

  const handleResolvedSubmit = (vals, helpers) => {
    const cleanedFields = (vals.fields || []).map((field) => {
      if (field?.type !== 'notification' || !field?.additional_fields?.notification) return field;
      return {
        ...field,
        additional_fields: {
          ...field.additional_fields,
          notification: cleanNotification(field.additional_fields.notification),
        },
      };
    });
    onSubmit({ ...vals, fields: cleanedFields, execution_rules: resolveExecutionRules(vals) }, helpers);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [value, setValue] = useState('');
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  const [selectedMachinePerTag, setSelectedMachinePerTag] = useState({});
  const [selectedCascaderPerTag, setSelectedCascaderPerTag] = useState({});
  const [tagsListPerMachine, setTagsListPerMachine] = useState({});
  // Guards the one-time default-machine prefill so re-renders don't re-apply it.
  const defaultPrefilledRef = react.useRef(false);

  // Fetch machine type+groups on mount
  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    }, process_id);
  }, [process_id]);

  // On edit: restore selectedMachinePerTag and fetch tags from saved machine_id in tags
  useEffect(() => {
    defaultPrefilledRef.current = false;
    const initialMachineMap = {};
    const initialCascaderMap = {};
    (values.fields || []).forEach((field, fieldIndex) => {
      if (field.type === 'button') {
        (field.additional_fields?.buttons || []).forEach((btn, buttonIndex) => {
          (btn.tags || []).forEach((tag, tagIndex) => {
            const tagKey = `${fieldIndex}-${buttonIndex}-${tagIndex}`;
            if (tag.machine_group_id) {
              initialMachineMap[tagKey] = tag.machine_group_id;
              initialCascaderMap[tagKey] = [tag.machine_id, tag.machine_group_id].filter(Boolean);
              fetchTagsForMachine(tag.machine_group_id, tagKey);
            } else if (tag.machine_id) {
              initialMachineMap[tagKey] = tag.machine_id;
              initialCascaderMap[tagKey] = [tag.machine_id];
              fetchTagsForMachine(tag.machine_id, tagKey);
            }
          });
        });
      }
      // Restore state for response field button tags
      (field.additional_fields?.notification?.response_fields || []).forEach((rf, rfi) => {
        if (rf.type === 'button') {
          (rf.additional_fields?.buttons || []).forEach((btn, bi) => {
            (btn.tags || []).forEach((tag, tagIndex) => {
              const tagKey = `rf-${fieldIndex}-${rfi}-${bi}-${tagIndex}`;
              if (tag.machine_group_id) {
                initialMachineMap[tagKey] = tag.machine_group_id;
                initialCascaderMap[tagKey] = [tag.machine_id, tag.machine_group_id].filter(Boolean);
                fetchTagsForMachine(tag.machine_group_id, tagKey);
              } else if (tag.machine_id) {
                initialMachineMap[tagKey] = tag.machine_id;
                initialCascaderMap[tagKey] = [tag.machine_id];
                fetchTagsForMachine(tag.machine_id, tagKey);
              }
            });
          });
        }
      });
    });
    if (Object.keys(initialMachineMap).length > 0) {
      setSelectedMachinePerTag(initialMachineMap);
      setSelectedCascaderPerTag(initialCascaderMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.action_id]);

  // Fetch parameters for a machine group (same API as Monitoring.js)
  const fetchTagsForMachine = (machineGroupId, tagKey) => {
    if (!machineGroupId) {
      setTagsListPerMachine((prev) => ({ ...prev, [tagKey]: [] }));
      return;
    }
    get_common_sub_categories_by_machine_group((res) => {
      setTagsListPerMachine((prev) => ({
        ...prev,
        [tagKey]: res?.data?.data || [],
      }));
    }, { machine_group_id: machineGroupId });
  };

  // Category → Group cascader options. When "Action List Default Machine" is on and
  // machines are configured in Machine Setup, restrict to the groups that actually
  // contain a configured machine.
  const machineCascaderOptions = (machineTypeWithGroups || [])
    .map((item) => {
      const groups = (item.machine_groups || []).filter((g) =>
        !defaultMachineSelection || !setupMachineIdSet.size
          ? true
          : (g.machines || []).some((m) => setupMachineIdSet.has(Number(m.machine_id)))
      );
      return {
        label: item.machine_category_name,
        value: item.machine_category_id,
        children: groups.length > 0
          ? groups.map((g) => ({ label: g.machine_group_name, value: g.machine_group_id }))
          : undefined,
      };
    })
    .filter((cat) =>
      !defaultMachineSelection || !setupMachineIdSet.size ? true : !!cat.children?.length
    );

  // Applies the default machine (category/group from Machine Setup) to a single tag.
  // Updates both the local cascader UI state and the Formik form values.
  const applyDefaultMachineToTag = (tagKey, fieldIndex, buttonIndex, tagIndex, setFieldValue) => {
    setSelectedCascaderPerTag((prev) => ({
      ...prev,
      [tagKey]: [defaultMachineTypeId, defaultMachineGroupId].filter(Boolean),
    }));
    setSelectedMachinePerTag((prev) => ({ ...prev, [tagKey]: defaultMachineGroupId }));
    const base = `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}]`;
    setFieldValue(`${base}.machine_id`, defaultMachineTypeId);
    setFieldValue(`${base}.machine_group_id`, defaultMachineGroupId);
    fetchTagsForMachine(defaultMachineGroupId, tagKey);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setValue('');
  };
  const add_actionlist_data_save = async (data) => {
    try {
      await add_actionlist_data(data, (res) => {
        if (res?.status === 'success') {
          toast.success(res.message);
          //      get_actionListData();
          setIsModalOpen(false);
          setValue('');
        } else {
          toast.warning(res.message);
        }
      });
    } catch (err) {
      console.error(err);
    }
  };
  const handleOk = () => {
    const req = {
      name: value,
    };
    add_actionlist_data_save(req);

    setIsModalOpen(false);
  };
  return (
    <div>
      <Formik
        initialValues={values}
        validationSchema={validationSchema}
        context={{ type: '' }}
        onSubmit={handleResolvedSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => {
          // Auto-prefill the default machine (from Machine Setup) into button tags that
          // have no machine chosen yet — only when "Action List Default Machine" is on.
          // eslint-disable-next-line react-hooks/rules-of-hooks
          react.useEffect(() => {
            if (defaultPrefilledRef.current) return;
            if (!defaultMachineSelection || !defaultMachineGroupId || !machineTypeWithGroups.length) return;
            let applied = false;
            (values.fields || []).forEach((field, fieldIndex) => {
              if (field.type !== 'button') return;
              (field.additional_fields?.buttons || []).forEach((btn, buttonIndex) => {
                (btn.tags || []).forEach((tag, tagIndex) => {
                  const tagKey = `${fieldIndex}-${buttonIndex}-${tagIndex}`;
                  if (selectedCascaderPerTag[tagKey] || tag.machine_group_id || tag.machine_id) return;
                  applyDefaultMachineToTag(tagKey, fieldIndex, buttonIndex, tagIndex, setFieldValue);
                  applied = true;
                });
              });
            });
            if (applied) defaultPrefilledRef.current = true;
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [defaultMachineSelection, defaultMachineGroupId, machineTypeWithGroups.length, values.fields]);

          // ── Rule-driven display-only helpers (read from lockedRuleFields with defaults) ──
          const ruleVal = (key, fallback) =>
            lockedRuleFields &&
            lockedRuleFields[key] !== undefined &&
            lockedRuleFields[key] !== null
              ? lockedRuleFields[key]
              : fallback;

          // Rule-driven select values (display-only)
          const actionTypeVal = ruleVal('action_type', 'Verification');
          const responseTypeVal = ruleVal('response_type', 'Yes / No');
          const executionModeVal = ruleVal('execution_mode', 'Manual');
          const isMachineAction = actionTypeVal === 'Machine Action';

          // Rule-driven runtime execution rules (display-only) — blocking + esign handled separately
          const runtimeRules = [
            { key: 'mandatory_step', label: 'Mandatory Step', def: true },
            { key: 'photo_capture_required', label: 'Photo Capture Required', def: false },
            { key: 'requires_supervisor_verification', label: 'Requires Supervisor Verification', def: false },
            { key: 'machine_status_validation', label: 'Machine Status Validation', def: false },
            { key: 'auto_completion_from_plc', label: 'Auto Completion From PLC', def: false },
          ];
          // Effective rule value: explicit per-item choice wins, else rule default
          const effRule = (key, def) => {
            const chosen = (values.execution_rules || {})[key];
            return chosen !== undefined && chosen !== null ? !!chosen : !!ruleVal(key, def);
          };
          const isMandatory = effRule('mandatory_step', true);
          // Resolve blocking from new field first, fall back to legacy boolean
          const blockingBehavior =
            (values.execution_rules || {}).blocking_behavior ||
            (effRule('block_next_step_until_complete', true) ? 'hard_block' : 'no_block');
          const isBlocking = blockingBehavior !== 'no_block';
          const isSoftBlock = blockingBehavior === 'soft_block';
          const isESign = !!(values.execution_rules || {}).requires_e_sign;

          // ── Execution Summary counts — derived from existing form data ──
          const totalSteps = (values.fields || []).length;
          const mandatorySteps = (values.fields || []).filter(
            (f) => f?.additional_fields?.required
          ).length;
          const blockingSteps = isBlocking ? totalSteps : 0;
          const eSignRequired = isESign ? 1 : 0;

          const sectionHeader = (text) => (
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{text}</p>
          );

          const executionFlowSteps = [
            'Operator receives action step',
            'Runtime validations executed',
            'Compliance checks and evidence captured',
            'Step completion updates operation runtime',
          ];

          return (
          <Form layout="vertical" onFinish={handleSubmit}>
            <div className="flex gap-5 items-start">
              {/* ===== LEFT: Action Step Configuration ===== */}
              <div className={`min-w-0 ${rulesEnabled ? 'w-3/4' : 'w-full'}`}>
                {/* ── Header ── */}
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-gray-800 m-0">Action Step Configuration</h2>
                  <p className="text-sm text-gray-500 m-0">
                    Configure verification and machine action steps with runtime execution rules
                  </p>
                </div>

                {/* Scrollable form area */}
                <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 6 }}>

                {/* ── Action Step Card ── */}
                <div
                  className="mb-4 rounded-lg border bg-white"
                  style={{ borderColor: '#e5e7eb', padding: '16px 18px' }}
                >
                  {/* Step header: circled number + name/subtitle + STATIC chips */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* STATIC display-only */}
                      <Badge count={1} style={{ backgroundColor: '#3b82f6', flexShrink: 0 }} />
                      <div>
                        <p className="text-base font-semibold text-gray-800 m-0">
                          {values.name || 'Action Step'}
                        </p>
                        <p className="text-xs text-gray-400 m-0">
                          {isMachineAction ? 'Machine Action Step' : 'Verification Action Step'}
                        </p>
                      </div>
                    </div>
                    {/* STATIC display-only chips — derived from rule values, not persisted */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isMandatory && (
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#b91c1c',
                            backgroundColor: '#fee2e2',
                          }}
                        >
                          REQUIRED
                        </span>
                      )}
                      {isBlocking && (
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 700,
                            color: isSoftBlock ? '#92400e' : '#15803d',
                            backgroundColor: isSoftBlock ? '#fef3c7' : '#dcfce7',
                          }}
                        >
                          {isSoftBlock ? 'SOFT BLOCK' : 'HARD BLOCK'}
                        </span>
                      )}
                      {isESign && (
                        <span
                          style={{
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#1d4ed8',
                            backgroundColor: '#dbeafe',
                          }}
                        >
                          E-SIGN
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Type + Response Type — rule-driven display-only (not persisted here) */}
                  <Row gutter={16} className="mb-2">
                    <Col span={12}>
                      {/* rule-driven display-only — not persisted here */}
                      <Form.Item label="Action Type" style={{ marginBottom: 0 }}>
                        <Select
                          disabled
                          value={actionTypeVal}
                          options={[
                            { value: 'Verification', label: 'Verification' },
                            { value: 'Machine Action', label: 'Machine Action' },
                            { value: 'Data Entry', label: 'Data Entry' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {/* rule-driven display-only — not persisted here */}
                      <Form.Item label="Response Type" style={{ marginBottom: 0 }}>
                        <Select
                          disabled
                          value={responseTypeVal}
                          options={[
                            { value: 'Yes / No', label: 'Yes / No' },
                            { value: 'Numeric', label: 'Numeric' },
                            { value: 'Text', label: 'Text' },
                            { value: 'Pass / Fail', label: 'Pass / Fail' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Execution Mode — rule-driven display-only, machine-action steps */}
                  {isMachineAction && (
                    <Row gutter={16} className="mb-2">
                      <Col span={12}>
                        {/* rule-driven display-only — not persisted here */}
                        <Form.Item label="Execution Mode" style={{ marginBottom: 0 }}>
                          <Select
                            disabled
                            value={executionModeVal}
                            options={[
                              { value: 'Manual', label: 'Manual' },
                              { value: 'Semi-Automatic', label: 'Semi-Automatic' },
                              { value: 'Automatic', label: 'Automatic' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {/* Operator Instruction — STATIC display-only (no new payload field) */}
                  <Row gutter={16} className="mb-3">
                    <Col span={24}>
                      {/* STATIC display-only — not persisted */}
                      <Form.Item label="Operator Instruction" style={{ marginBottom: 0 }}>
                        <Input.TextArea
                          rows={2}
                          disabled
                          placeholder="Operator instruction (configured in rules)"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* ── Blocking Behavior + eSign — always visible core controls ── */}
                  <div
                    className="rounded-lg border mb-3"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '12px 14px' }}
                  >
                    <p className="text-xs font-semibold text-gray-500 mb-3 m-0">
                      Compliance Controls
                    </p>
                    <Row gutter={[16, 12]}>
                      {/* Blocking Behavior */}
                      <Col span={16}>
                        <Form.Item label="Blocking Behavior" style={{ marginBottom: 0 }}>
                          {(() => {
                            const blockingVal = (values.execution_rules || {}).blocking_behavior ||
                              ((values.execution_rules || {}).block_next_step_until_complete
                                ? 'hard_block' : 'no_block');
                            const blockingOptions = [
                              { value: 'no_block', label: 'No Block', color: '#6b7280', bg: '#f3f4f6' },
                              { value: 'hard_block', label: 'Hard Block', color: '#dc2626', bg: '#fee2e2' },
                              { value: 'soft_block', label: 'Soft Block + Deviation', color: '#d97706', bg: '#fef3c7' },
                            ];
                            return (
                              <div className="flex gap-2">
                                {blockingOptions.map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      setFieldValue('execution_rules', {
                                        ...(values.execution_rules || {}),
                                        blocking_behavior: opt.value,
                                        block_next_step_until_complete:
                                          opt.value === 'hard_block' || opt.value === 'soft_block',
                                      });
                                    }}
                                    style={{
                                      padding: '5px 12px',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      border: `1.5px solid ${blockingVal === opt.value ? opt.color : '#e5e7eb'}`,
                                      backgroundColor: blockingVal === opt.value ? opt.bg : '#fff',
                                      color: blockingVal === opt.value ? opt.color : '#9ca3af',
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                          {(values.execution_rules || {}).blocking_behavior === 'soft_block' && (
                            <p className="text-xs text-amber-600 mt-1 m-0">
                              Operator must supply a QMS Deviation No. + Justification at runtime to proceed.
                            </p>
                          )}
                        </Form.Item>
                      </Col>

                      {/* eSign Required */}
                      <Col span={8}>
                        <Form.Item label="E-Signature Required" style={{ marginBottom: 0 }}>
                          <div className="flex items-center gap-2 mt-1">
                            <Switch
                              checked={!!((values.execution_rules || {}).requires_e_sign)}
                              checkedChildren="Required"
                              unCheckedChildren="Not Required"
                              onChange={(checked) =>
                                setFieldValue('execution_rules', {
                                  ...(values.execution_rules || {}),
                                  requires_e_sign: checked,
                                })
                              }
                              style={{
                                backgroundColor: (values.execution_rules || {}).requires_e_sign
                                  ? '#1d4ed8' : undefined,
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  {/* Runtime Execution Rules — hidden when Rules app-setting is off */}
                  {rulesEnabled && <div
                    className="rounded-lg border mb-2"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '12px 14px' }}
                  >
                    <p className="text-xs font-semibold text-gray-500 mb-3 m-0">
                      Runtime Execution Rules
                    </p>
                    <Row gutter={[16, 10]}>
                      {runtimeRules.map(({ key, label, def }) => {
                        // Effective value: explicit per-item choice wins, else rule default
                        const chosen = (values.execution_rules || {})[key];
                        const checked =
                          chosen !== undefined && chosen !== null ? !!chosen : !!ruleVal(key, def);
                        return (
                          <Col span={12} key={key}>
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                setFieldValue('execution_rules', {
                                  ...(values.execution_rules || {}),
                                  [key]: e.target.checked,
                                })
                              }
                            >
                              {label}
                            </Checkbox>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>}
                </div>

                {/* ── Action Configuration (existing wired fields preserved) ── */}
                <div
                  className="mb-4 rounded-lg border bg-white"
                  style={{ borderColor: '#e5e7eb', padding: '16px 18px' }}
                >
                  {sectionHeader('Action Configuration')}
                  <Row gutter={24}>
                  {/* Action Select */}
                  <Col span={12}>
                    <Form.Item
                      label="Action"
                      validateStatus={touched.action_id && errors.action_id ? 'error' : ''}
                      help={touched.action_id && errors.action_id}
                    >
                      <Space.Compact style={{ width: '100%' }}>
                        <Select
                          placeholder="Select Action"
                          value={values.action_id || null}
                          onChange={(val, option) => {
                            setFieldValue('action_id', val || '');
                            if (val) setFieldValue('name', option?.label || '');
                          }}
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={actions_field_list?.map((action) => ({
                            value: action.id,
                            label: action.name,
                          }))}
                        />
                        {/* <Tooltip title="Add Action">
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => setIsModalOpen(!isModalOpen)}
                          />
                        </Tooltip> */}
                      </Space.Compact>
                    </Form.Item>
                  </Col>

                  {/* Fields Array */}
                  <Col span={24}>
                <FieldArray name="fields">
                  {({ push: pushField, remove: removeField }) => (
                    <div className="flex flex-col gap-4">
                      {values.fields.map((field, fieldIndex) => (
                        <div
                          key={fieldIndex}
                          className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                        >
                          {/* Field Header */}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Field {fieldIndex + 1}
                            </span>
                            {values.fields.length > 1 && (
                              <Button
                                danger
                                size="small"
                                onClick={() => removeField(fieldIndex)}
                                icon={<MinusCircleOutlined />}
                              >
                                Remove Field
                              </Button>
                            )}
                          </div>

                          {/* Hidden field name — kept in request but not shown in form */}
                          <input
                            type="hidden"
                            name={`fields[${fieldIndex}].name`}
                            value={field.name || ''}
                            onChange={handleChange}
                          />

                          <Row gutter={[16, 16]}>

                            {/* Field Type */}
                            <Col span={8}>
                              <Form.Item
                                label="Field Type"
                                validateStatus={
                                  touched.fields?.[fieldIndex]?.type &&
                                    errors.fields?.[fieldIndex]?.type
                                    ? 'error'
                                    : ''
                                }
                                help={
                                  touched.fields?.[fieldIndex]?.type &&
                                  errors.fields?.[fieldIndex]?.type
                                }
                              >
                                <Select
                                  value={field.type || null}
                                  onChange={(val) => {
                                    setFieldValue(`fields[${fieldIndex}].type`, val || '');
                                    if (['dropdown', 'radio'].includes(val)) {
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.options`,
                                        [{ label: '', value: '' }]
                                      );
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.buttons`,
                                        []
                                      );
                                    } else if (val === 'button') {
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.buttons`,
                                        [
                                          {
                                            label: '',
                                            color: '#1677ff',
                                            hidden: false,
                                            auto: false,
                                            tags: [
                                              {
                                                tag_name: null,
                                                parameter_name: null,
                                                machine_id: null,
                                                data_type: '',
                                                value: '',
                                                read_write: 'read',
                                              },
                                            ],
                                          },
                                        ]
                                      );
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.options`,
                                        []
                                      );
                                    } else if (val === 'notification') {
                                      // Seed an inline notification config for this field.
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.notification`,
                                        { ...emptyNotification }
                                      );
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.options`,
                                        []
                                      );
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.buttons`,
                                        []
                                      );
                                    } else {
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.options`,
                                        []
                                      );
                                      setFieldValue(
                                        `fields[${fieldIndex}].additional_fields.buttons`,
                                        []
                                      );
                                    }
                                  }}
                                  showSearch
                                  allowClear
                                  optionFilterProp="children"
                                  filterOption={(input, option) =>
                                    (option?.label ?? '')
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  options={fieldTypes.map((type) => ({
                                    value: type.value,
                                    label: type.label,
                                  }))}
                                />
                              </Form.Item>
                            </Col>

                            {/* Required */}
                            <Col span={8}>
                              <Form.Item label="Required Field">
                                <Checkbox
                                  checked={field.additional_fields?.required || false}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `fields[${fieldIndex}].additional_fields.required`,
                                      e.target.checked
                                    )
                                  }
                                >
                                  Yes
                                </Checkbox>
                              </Form.Item>
                            </Col>

                            {/* Options Section */}
                            {(field.type === 'dropdown' || field.type === 'radio') && (
                              <Col span={24}>
                                <Form.Item label="Options">
                                  <FieldArray
                                    name={`fields[${fieldIndex}].additional_fields.options`}
                                  >
                                    {({ push, remove }) => (
                                      <div className="flex flex-col gap-2">
                                        {field.additional_fields?.options?.map((opt, index) => (
                                          <Row key={index} gutter={[16, 16]} align="middle">
                                            <Col span={9}>
                                              <Form.Item
                                                validateStatus={
                                                  touched.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.label &&
                                                    errors.fields?.[fieldIndex]?.additional_fields
                                                      ?.options?.[index]?.label
                                                    ? 'error'
                                                    : ''
                                                }
                                                help={
                                                  touched.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.label &&
                                                  errors.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.label
                                                }
                                              >
                                                <Input
                                                  placeholder="Label"
                                                  name={`fields[${fieldIndex}].additional_fields.options[${index}].label`}
                                                  value={opt.label}
                                                  onChange={handleChange}
                                                />
                                              </Form.Item>
                                            </Col>

                                            <Col span={9}>
                                              <Form.Item
                                                validateStatus={
                                                  touched.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.value &&
                                                    errors.fields?.[fieldIndex]?.additional_fields
                                                      ?.options?.[index]?.value
                                                    ? 'error'
                                                    : ''
                                                }
                                                help={
                                                  touched.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.value &&
                                                  errors.fields?.[fieldIndex]?.additional_fields
                                                    ?.options?.[index]?.value
                                                }
                                              >
                                                <Input
                                                  placeholder="Value"
                                                  name={`fields[${fieldIndex}].additional_fields.options[${index}].value`}
                                                  value={opt.value}
                                                  onChange={handleChange}
                                                />
                                              </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                              {field.additional_fields?.options?.length > 1 && (
                                                <Button
                                                  danger
                                                  onClick={() => remove(index)}
                                                  icon={<MinusCircleOutlined />}
                                                />
                                              )}
                                            </Col>
                                          </Row>
                                        ))}

                                        <Button
                                          type="dashed"
                                          onClick={() => push({ label: '', value: '' })}
                                          icon={<PlusOutlined />}
                                          className="w-full"
                                        >
                                          Add Option
                                        </Button>
                                      </div>
                                    )}
                                  </FieldArray>
                                </Form.Item>
                              </Col>
                            )}

                            {/* Notification field type — structured condition + notify config */}
                            {field.type === 'notification' && (() => {
                              const notif = field.additional_fields?.notification || emptyNotification;
                              const base = `fields[${fieldIndex}].additional_fields.notification`;
                              const src = notif.condition_source || 'none';
                              const isBetween = notif.threshold_operator === 'between';
                              return (
                                <Col span={24}>
                                  <div
                                    className="rounded-lg border bg-white"
                                    style={{ borderColor: '#e5e7eb', padding: '12px 14px' }}
                                  >
                                    {/* ── 1. Trigger Condition ── */}
                                    <div
                                      className="rounded-md mb-3"
                                      style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 12px 26px 12px' }}
                                    >
                                      <p className="text-xs font-semibold text-gray-500 mb-2 m-0">Trigger Condition</p>
                                      <Row gutter={10} align="bottom" wrap>
                                        <Col flex="0 0 200px">
                                          <Form.Item label="Source" style={{ marginBottom: 0 }}>
                                            <Select
                                              className="w-full"
                                              value={src}
                                              onChange={(val) => setFieldValue(`${base}.condition_source`, val)}
                                              options={operation_id
                                                ? CONDITION_SOURCES
                                                : CONDITION_SOURCES.filter((s) => s.value !== 'prior_action')}
                                            />
                                          </Form.Item>
                                        </Col>

                                        {src === 'duration' && (<>
                                          <Col flex="0 0 140px">
                                            <Form.Item label="Basis" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.duration_basis || 'absolute'}
                                                onChange={(val) => setFieldValue(`${base}.duration_basis`, val)}
                                                options={DURATION_BASIS}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 170px">
                                            <Form.Item label="Operator" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.duration_operator || 'exceeded'}
                                                onChange={(val) => setFieldValue(`${base}.duration_operator`, val)}
                                                options={DURATION_OPERATORS}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="1">
                                            {(() => {
                                              const isPercent = (notif.duration_basis || 'absolute') === 'percent';
                                              const calcMins = isPercent && maxDurMins != null && notif.duration_value != null
                                                ? Math.round((notif.duration_value / 100) * maxDurMins)
                                                : null;
                                              const hint = calcMins != null
                                                ? `≈ ${calcMins} min (of ${maxDurMins} min max)`
                                                : isPercent && !maxDurMins
                                                  ? 'Set operation max duration to see minutes'
                                                  : null;
                                              return (
                                                <div style={{ position: 'relative' }}>
                                                  <Form.Item
                                                    label={isPercent ? '% of Max Duration' : 'Value'}
                                                    style={{ marginBottom: 0 }}
                                                  >
                                                    <Space.Compact className="w-full">
                                                      <Input type="number" min={0} max={isPercent ? 100 : undefined}
                                                        placeholder={isPercent ? '0–100' : 'Value'}
                                                        value={notif.duration_value ?? ''}
                                                        onChange={(e) => setFieldValue(`${base}.duration_value`,
                                                          e.target.value === '' ? null : Number(e.target.value))}
                                                        addonAfter={isPercent ? '%' : undefined}
                                                      />
                                                      {!isPercent && (
                                                        <Select style={{ width: 100 }}
                                                          value={notif.duration_unit || 'min'}
                                                          onChange={(val) => setFieldValue(`${base}.duration_unit`, val)}
                                                          options={DURATION_UNITS}
                                                        />
                                                      )}
                                                    </Space.Compact>
                                                  </Form.Item>
                                                  {hint && (
                                                    <div style={{ position: 'absolute', top: '100%', left: 0, fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', marginTop: 1 }}>{hint}</div>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                          </Col>
                                        </>)}

                                        {src === 'ingredient' && (<>
                                          <Col flex="0 0 180px">
                                            <Form.Item label="Ingredient" style={{ marginBottom: 0 }}>
                                              <Select className="w-full" placeholder="Select ingredient"
                                                value={notif.ingredient_code || null} allowClear showSearch optionFilterProp="label"
                                                onChange={(val, opt) => {
                                                  setFieldValue(`${base}.ingredient_code`, val || null);
                                                  setFieldValue(`${base}.ingredient_unit`, opt?.unit || '');
                                                }}
                                                options={(operation_ingredients || []).map((i) => ({
                                                  value: i.ingredient_code,
                                                  label: Array.isArray(i.ingredient) ? i.ingredient[0] : (i.ingredient ?? i.ingredient_code ?? ''),
                                                  unit: i.unit ?? i.uom ?? '',
                                                }))}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 130px">
                                            <Form.Item label="Basis" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.threshold_basis || 'absolute'}
                                                onChange={(val) => setFieldValue(`${base}.threshold_basis`, val)}
                                                options={[{ value: 'absolute', label: 'Absolute' }, { value: 'percent', label: '% of Expected' }]}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 160px">
                                            <Form.Item label="Operator" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.threshold_operator || '>'}
                                                onChange={(val) => {
                                                  setFieldValue(`${base}.threshold_operator`, val);
                                                  if (val !== 'between') setFieldValue(`${base}.threshold_value_max`, null);
                                                }}
                                                options={THRESHOLD_OPERATORS}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="1">
                                            <Form.Item label={isBetween ? 'Min / Max' : 'Value'} style={{ marginBottom: 0 }}>
                                              {isBetween ? (
                                                <Space.Compact className="w-full">
                                                  <Input type="number" placeholder="Min"
                                                    value={notif.threshold_value ?? ''}
                                                    onChange={(e) => setFieldValue(`${base}.threshold_value`,
                                                      e.target.value === '' ? null : Number(e.target.value))} />
                                                  <Input type="number" placeholder="Max"
                                                    value={notif.threshold_value_max ?? ''}
                                                    onChange={(e) => setFieldValue(`${base}.threshold_value_max`,
                                                      e.target.value === '' ? null : Number(e.target.value))} />
                                                </Space.Compact>
                                              ) : (
                                                <Input type="number" placeholder="Value"
                                                  value={notif.threshold_value ?? ''}
                                                  onChange={(e) => setFieldValue(`${base}.threshold_value`,
                                                    e.target.value === '' ? null : Number(e.target.value))}
                                                  addonAfter={notif.ingredient_unit || undefined}
                                                />
                                              )}
                                            </Form.Item>
                                          </Col>
                                        </>)}

                                        {src === 'monitoring' && (<>
                                          <Col flex="0 0 180px">
                                            <Form.Item label="Parameter" style={{ marginBottom: 0 }}>
                                              <Select className="w-full" placeholder="Select parameter"
                                                value={notif.monitoring_parameter_code || null} allowClear showSearch optionFilterProp="label"
                                                onChange={(val, opt) => {
                                                  setFieldValue(`${base}.monitoring_parameter_code`, val || null);
                                                  setFieldValue(`${base}.monitoring_unit`, opt?.unit || '');
                                                }}
                                                options={(operation_monitoring || []).map((m) => ({
                                                  value: m.parameter_code,
                                                  label: m.parameter ?? m.parameter_name ?? m.parameter_code ?? '',
                                                  unit: m.unit ?? m.parameter_unit ?? '',
                                                }))}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 130px">
                                            <Form.Item label="Basis" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.threshold_basis || 'absolute'}
                                                onChange={(val) => setFieldValue(`${base}.threshold_basis`, val)}
                                                options={[{ value: 'absolute', label: 'Absolute' }, { value: 'percent', label: '% of Limit' }]}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 160px">
                                            <Form.Item label="Operator" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.threshold_operator || '>'}
                                                onChange={(val) => {
                                                  setFieldValue(`${base}.threshold_operator`, val);
                                                  if (val !== 'between') setFieldValue(`${base}.threshold_value_max`, null);
                                                }}
                                                options={THRESHOLD_OPERATORS}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="1">
                                            <Form.Item label={isBetween ? 'Min / Max' : 'Value'} style={{ marginBottom: 0 }}>
                                              {isBetween ? (
                                                <Space.Compact className="w-full">
                                                  <Input type="number" placeholder="Min"
                                                    value={notif.threshold_value ?? ''}
                                                    onChange={(e) => setFieldValue(`${base}.threshold_value`,
                                                      e.target.value === '' ? null : Number(e.target.value))} />
                                                  <Input type="number" placeholder="Max"
                                                    value={notif.threshold_value_max ?? ''}
                                                    onChange={(e) => setFieldValue(`${base}.threshold_value_max`,
                                                      e.target.value === '' ? null : Number(e.target.value))} />
                                                </Space.Compact>
                                              ) : (
                                                <Input type="number" placeholder="Value"
                                                  value={notif.threshold_value ?? ''}
                                                  onChange={(e) => setFieldValue(`${base}.threshold_value`,
                                                    e.target.value === '' ? null : Number(e.target.value))}
                                                  addonAfter={notif.monitoring_unit || undefined}
                                                />
                                              )}
                                            </Form.Item>
                                          </Col>
                                        </>)}

                                        {src === 'sampling' && (
                                          <Col flex="0 0 150px">
                                            <Form.Item label="Expected Result" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.sampling_expected || 'pass'}
                                                onChange={(val) => setFieldValue(`${base}.sampling_expected`, val)}
                                                options={[{ value: 'pass', label: 'Pass' }, { value: 'fail', label: 'Fail' }]}
                                              />
                                            </Form.Item>
                                          </Col>
                                        )}

                                        {src === 'prior_action' && (<>
                                          <Col flex="0 0 220px">
                                            <Form.Item label="Action Step" style={{ marginBottom: 0 }}>
                                              <Select className="w-full" placeholder="Select prior action step"
                                                value={notif.prior_action_step_id || null} allowClear showSearch optionFilterProp="label"
                                                onChange={(val) => setFieldValue(`${base}.prior_action_step_id`, val || null)}
                                                options={operation_action_steps.map((s) => ({ value: s.id, label: s.name }))}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col flex="0 0 150px">
                                            <Form.Item label="Expected Response" style={{ marginBottom: 0 }}>
                                              <Select className="w-full"
                                                value={notif.prior_action_expected || 'yes'}
                                                onChange={(val) => setFieldValue(`${base}.prior_action_expected`, val)}
                                                options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                                              />
                                            </Form.Item>
                                          </Col>
                                        </>)}
                                      </Row>
                                    </div>

                                    {/* ── 2. Timing (Notification Only) ── */}
                                    {src === 'none' && (
                                      <Row gutter={12} className="mb-2">
                                        <Col span={notif.timing === 'after_duration' ? 12 : 24}>
                                          <Form.Item label="When" style={{ marginBottom: 0 }}>
                                            <Select className="w-full"
                                              value={notif.timing || 'at_start'}
                                              onChange={(val) => setFieldValue(`${base}.timing`, val)}
                                              options={NOTIFICATION_TIMING}
                                            />
                                          </Form.Item>
                                        </Col>
                                        {notif.timing === 'after_duration' && (
                                          <Col span={12}>
                                            <Form.Item label="After" style={{ marginBottom: 0 }}>
                                              <Space.Compact className="w-full">
                                                <Input type="number" min={0} placeholder="Value"
                                                  value={notif.timing_duration_value ?? ''}
                                                  onChange={(e) => setFieldValue(`${base}.timing_duration_value`,
                                                    e.target.value === '' ? null : Number(e.target.value))}
                                                />
                                                <Select style={{ width: 110 }}
                                                  value={notif.timing_duration_unit || 'min'}
                                                  onChange={(val) => setFieldValue(`${base}.timing_duration_unit`, val)}
                                                  options={DURATION_UNITS}
                                                />
                                              </Space.Compact>
                                            </Form.Item>
                                          </Col>
                                        )}
                                      </Row>
                                    )}

                                    {/* ── 3. Severity + Notify Role + Channels ── */}
                                    <Row gutter={12} className="mb-2">
                                      <Col span={8}>
                                        <Form.Item label="Severity" style={{ marginBottom: 0 }}>
                                          <Select className="w-full" value={notif.severity}
                                            onChange={(val) => setFieldValue(`${base}.severity`, val)}
                                            options={NOTIFY_SEVERITY}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <Form.Item label="Notify Roles" style={{ marginBottom: 0 }}>
                                          <Select
                                            mode="multiple"
                                            className="w-full"
                                            placeholder="Select roles…"
                                            value={notif.notify_role_ids || []}
                                            onChange={(val) => setFieldValue(`${base}.notify_role_ids`, val)}
                                            allowClear showSearch optionFilterProp="label"
                                            options={roles.map((r) => ({ value: r.role ?? r.role_name ?? r.name, label: r.role ?? r.role_name ?? r.name }))}
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <Form.Item label="Channels" style={{ marginBottom: 0 }}>
                                          <Select mode="multiple" className="w-full"
                                            value={notif.channels || []}
                                            onChange={(val) => setFieldValue(`${base}.channels`, val)}
                                            options={NOTIFY_CHANNELS}
                                          />
                                        </Form.Item>
                                      </Col>
                                    </Row>

                                    {/* ── 4. Message with HTML toggle ── */}
                                    <Form.Item
                                      label={
                                        <div className="flex items-center gap-2">
                                          <span>Message</span>
                                          <Switch size="small"
                                            checked={!!notif.message_is_html}
                                            checkedChildren="HTML"
                                            unCheckedChildren="Text"
                                            onChange={(val) => setFieldValue(`${base}.message_is_html`, val)}
                                          />
                                        </div>
                                      }
                                      style={{ marginBottom: 8 }}
                                    >
                                      <Input.TextArea
                                        rows={2}
                                        placeholder={notif.message_is_html
                                          ? '<b>Alert:</b> Threshold exceeded'
                                          : 'Optional alert message…'}
                                        value={notif.message || ''}
                                        onChange={(e) => setFieldValue(`${base}.message`, e.target.value)}
                                        style={notif.message_is_html ? { fontFamily: 'monospace', fontSize: 12 } : undefined}
                                      />
                                    </Form.Item>

                                    {/* ── 5. Response Fields ── */}
                                    <FieldArray name={`${base}.response_fields`}>
                                      {({ push: pushRF, remove: removeRF }) => (
                                        <div className="flex flex-col gap-4">
                                          <div className="flex items-center justify-between">
                                            <p className="text-xs font-semibold text-gray-500 m-0">Response Fields</p>
                                            <Button
                                              type="dashed"
                                              size="small"
                                              icon={<PlusOutlined />}
                                              onClick={() => pushRF({ ...emptyField })}
                                            >
                                              Add Response Field
                                            </Button>
                                          </div>
                                          {(notif.response_fields || []).length === 0 && (
                                            <p className="text-xs text-gray-400 m-0">No response fields — add fields the operator must fill after this notification fires.</p>
                                          )}
                                          {(notif.response_fields || []).map((rf, rfi) => {
                                            const rfBase = `${base}.response_fields[${rfi}]`;
                                            return (
                                              <div key={rfi} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                {/* Header */}
                                                <div className="flex items-center justify-between mb-3">
                                                  <span className="text-sm font-semibold text-gray-700">Field {rfi + 1}</span>
                                                  <Button danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeRF(rfi)}>
                                                    Remove Field
                                                  </Button>
                                                </div>
                                                <Row gutter={[16, 16]}>
                                                  {/* Field Type */}
                                                  <Col span={8}>
                                                    <Form.Item label="Field Type">
                                                      <Select
                                                        value={rf.type || null}
                                                        onChange={(val) => {
                                                          setFieldValue(`${rfBase}.type`, val || '');
                                                          if (['dropdown', 'radio'].includes(val)) {
                                                            setFieldValue(`${rfBase}.additional_fields.options`, [{ label: '', value: '' }]);
                                                            setFieldValue(`${rfBase}.additional_fields.buttons`, []);
                                                          } else if (val === 'button') {
                                                            setFieldValue(`${rfBase}.additional_fields.buttons`, [{ label: '', color: '#1677ff', hidden: false, auto: false }]);
                                                            setFieldValue(`${rfBase}.additional_fields.options`, []);
                                                          } else {
                                                            setFieldValue(`${rfBase}.additional_fields.options`, []);
                                                            setFieldValue(`${rfBase}.additional_fields.buttons`, []);
                                                          }
                                                        }}
                                                        showSearch allowClear optionFilterProp="label"
                                                        options={fieldTypes.filter((t) => t.value !== 'notification').map((t) => ({ value: t.value, label: t.label }))}
                                                      />
                                                    </Form.Item>
                                                  </Col>
                                                  {/* Required */}
                                                  <Col span={8}>
                                                    <Form.Item label="Required Field">
                                                      <Checkbox
                                                        checked={rf.additional_fields?.required || false}
                                                        onChange={(e) => setFieldValue(`${rfBase}.additional_fields.required`, e.target.checked)}
                                                      >
                                                        Yes
                                                      </Checkbox>
                                                    </Form.Item>
                                                  </Col>

                                                  {/* Options — dropdown / radio */}
                                                  {(rf.type === 'dropdown' || rf.type === 'radio') && (
                                                    <Col span={24}>
                                                      <Form.Item label="Options">
                                                        <FieldArray name={`${rfBase}.additional_fields.options`}>
                                                          {({ push: pushOpt, remove: removeOpt }) => (
                                                            <div className="flex flex-col gap-2">
                                                              {(rf.additional_fields?.options || []).map((opt, oi) => (
                                                                <Row key={oi} gutter={[16, 16]} align="middle">
                                                                  <Col span={9}>
                                                                    <Input
                                                                      placeholder="Label"
                                                                      value={opt.label}
                                                                      onChange={(e) => setFieldValue(`${rfBase}.additional_fields.options[${oi}].label`, e.target.value)}
                                                                    />
                                                                  </Col>
                                                                  <Col span={9}>
                                                                    <Input
                                                                      placeholder="Value"
                                                                      value={opt.value}
                                                                      onChange={(e) => setFieldValue(`${rfBase}.additional_fields.options[${oi}].value`, e.target.value)}
                                                                    />
                                                                  </Col>
                                                                  <Col span={4}>
                                                                    {(rf.additional_fields?.options || []).length > 1 && (
                                                                      <Button danger onClick={() => removeOpt(oi)} icon={<MinusCircleOutlined />} />
                                                                    )}
                                                                  </Col>
                                                                </Row>
                                                              ))}
                                                              <Button type="dashed" onClick={() => pushOpt({ label: '', value: '' })} icon={<PlusOutlined />} className="w-full">
                                                                Add Option
                                                              </Button>
                                                            </div>
                                                          )}
                                                        </FieldArray>
                                                      </Form.Item>
                                                    </Col>
                                                  )}

                                                  {/* Buttons — button type */}
                                                  {rf.type === 'button' && (
                                                    <Col span={24}>
                                                      <Form.Item label="Buttons">
                                                        <FieldArray name={`${rfBase}.additional_fields.buttons`}>
                                                          {({ push: pushBtn, remove: removeBtn }) => (
                                                            <div className="flex flex-col gap-3">
                                                              {(rf.additional_fields?.buttons || []).map((btn, bi) => (
                                                                <div key={bi} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                                                                  <div className="flex items-stretch gap-0">
                                                                    {/* Live preview */}
                                                                    <div
                                                                      className="flex flex-col items-center justify-center gap-1.5 px-4 py-3 flex-shrink-0 min-w-[100px] border-r border-gray-200"
                                                                      style={{ backgroundColor: (btn.color || '#1677ff') + '15' }}
                                                                    >
                                                                      <div
                                                                        className="px-3 py-1.5 rounded-md text-white text-sm font-semibold"
                                                                        style={{ backgroundColor: btn.color || '#1677ff' }}
                                                                      >
                                                                        {btn.label || 'Button'}
                                                                      </div>
                                                                      <div className="flex gap-1">
                                                                        <span className={`text-xs px-1 py-0.5 rounded font-medium ${btn.hidden ? 'bg-gray-300 text-gray-700' : 'bg-gray-100 text-gray-400'}`}>
                                                                          {btn.hidden ? 'Hidden' : 'Visible'}
                                                                        </span>
                                                                        {btn.auto && <span className="text-xs px-1 py-0.5 rounded font-medium bg-blue-100 text-blue-700">Auto</span>}
                                                                      </div>
                                                                    </div>
                                                                    {/* Config fields */}
                                                                    <div className="flex-1 p-3">
                                                                      <Row gutter={[12, 8]} align="middle">
                                                                        <Col span={8}>
                                                                          <Form.Item label="Label" style={{ marginBottom: 0 }}>
                                                                            <Input size="small"
                                                                              placeholder="Button Label"
                                                                              value={btn.label}
                                                                              onChange={(e) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].label`, e.target.value)}
                                                                            />
                                                                          </Form.Item>
                                                                        </Col>
                                                                        <Col span={10}>
                                                                          <Form.Item label="Color" style={{ marginBottom: 0 }}>
                                                                            <Space.Compact className="w-full">
                                                                              <ColorPicker size="small"
                                                                                value={btn.color || '#1677ff'}
                                                                                onChange={(color) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].color`, color.toHexString())}
                                                                              />
                                                                              <Input size="small"
                                                                                value={btn.color || ''}
                                                                                placeholder="#1677ff"
                                                                                onChange={(e) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].color`, e.target.value)}
                                                                              />
                                                                            </Space.Compact>
                                                                          </Form.Item>
                                                                        </Col>
                                                                        <Col span={3}>
                                                                          <Form.Item label="Hidden" style={{ marginBottom: 0 }}>
                                                                            <Switch size="small"
                                                                              checked={btn.hidden || false}
                                                                              onChange={(v) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].hidden`, v)}
                                                                            />
                                                                          </Form.Item>
                                                                        </Col>
                                                                        <Col span={3}>
                                                                          <Form.Item label="Auto" style={{ marginBottom: 0 }}>
                                                                            <Switch size="small"
                                                                              checked={btn.auto || false}
                                                                              onChange={(v) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].auto`, v)}
                                                                            />
                                                                          </Form.Item>
                                                                        </Col>
                                                                      </Row>
                                                                    </div>
                                                                    {/* Remove */}
                                                                    <div className="flex items-center px-2 border-l border-gray-100">
                                                                      <Button danger size="small" type="text" onClick={() => removeBtn(bi)} icon={<MinusCircleOutlined />} />
                                                                    </div>
                                                                  </div>
                                                                  {/* Tags Section */}
                                                                  <div className="pl-4 pr-3 pb-3 border-t border-gray-100">
                                                                    <FieldArray name={`${rfBase}.additional_fields.buttons[${bi}].tags`}>
                                                                      {({ push: pushRfTag, remove: removeRfTag }) => (
                                                                        <div className="flex flex-col gap-2 mt-2">
                                                                          <div className="flex items-center justify-between">
                                                                            <p className="text-xs font-semibold text-gray-600 m-0">Tags</p>
                                                                            <Button size="small" type="dashed" icon={<PlusOutlined />}
                                                                              onClick={() => pushRfTag({ tag_name: null, data_type: '', read_write: null, value: '', machine_id: null, machine_group_id: null, parameter_name: null })}>
                                                                              Add Tag
                                                                            </Button>
                                                                          </div>
                                                                          {(btn.tags || []).map((tag, tagIndex) => {
                                                                            const tagKey = `rf-${fieldIndex}-${rfi}-${bi}-${tagIndex}`;
                                                                            return (
                                                                              <div key={tagIndex} className="p-3 border border-blue-200 rounded-md bg-blue-50">
                                                                                <Row gutter={[12, 12]} align="middle">
                                                                                  <Col span={6}>
                                                                                    <Form.Item label="Machine Type">
                                                                                      <Cascader
                                                                                        placeholder="Select Machine Type"
                                                                                        showSearch allowClear style={{ width: '100%' }}
                                                                                        popupClassName="cascader-wide-dropdown"
                                                                                        value={selectedCascaderPerTag[tagKey] || undefined}
                                                                                        onChange={(val) => {
                                                                                          setSelectedCascaderPerTag((prev) => ({ ...prev, [tagKey]: val || null }));
                                                                                          const machineTypeId = val?.[0] || null;
                                                                                          const machineGroupId = val?.[1] || null;
                                                                                          setSelectedMachinePerTag((prev) => ({ ...prev, [tagKey]: machineGroupId || machineTypeId }));
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].machine_id`, machineTypeId);
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].machine_group_id`, machineGroupId);
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].tag_name`, null);
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].parameter_name`, null);
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].data_type`, null);
                                                                                          fetchTagsForMachine(machineGroupId || machineTypeId, tagKey);
                                                                                        }}
                                                                                        options={machineCascaderOptions}
                                                                                      />
                                                                                    </Form.Item>
                                                                                  </Col>
                                                                                  <Col span={5}>
                                                                                    <Form.Item label="Parameter Name">
                                                                                      <Select
                                                                                        placeholder="Select Parameter"
                                                                                        value={tag.parameter_name || null}
                                                                                        disabled={!selectedMachinePerTag[tagKey]}
                                                                                        showSearch allowClear optionFilterProp="label"
                                                                                        onChange={(val) => {
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].parameter_name`, val || null);
                                                                                          const tagsList = tagsListPerMachine[tagKey] || [];
                                                                                          const selectedParam = tagsList.find((p) => p.parameter_name === val);
                                                                                          if (selectedParam) {
                                                                                            const dtList = Array.isArray(selectedParam.data_type) ? selectedParam.data_type : (selectedParam.data_type ? [selectedParam.data_type] : []);
                                                                                            setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].data_type`, dtList.length === 1 ? dtList[0] : (dtList[0] || ''));
                                                                                            setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].tag_name`, val);
                                                                                          } else {
                                                                                            setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].data_type`, '');
                                                                                            setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].tag_name`, null);
                                                                                          }
                                                                                        }}
                                                                                        options={(tagsListPerMachine[tagKey] || [])
                                                                                          .filter((p) => !(btn.tags || []).some((t, idx) => idx !== tagIndex && t.parameter_name === p.parameter_name))
                                                                                          .map((p) => ({ value: p.parameter_name, label: p.parameter_name }))}
                                                                                      />
                                                                                    </Form.Item>
                                                                                  </Col>
                                                                                  <Col span={4}>
                                                                                    <Form.Item label="Data Type">
                                                                                      <Select
                                                                                        placeholder="Auto-filled"
                                                                                        value={tag.data_type || null}
                                                                                        disabled={!!tag.parameter_name}
                                                                                        onChange={(val) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].data_type`, val)}
                                                                                        options={[
                                                                                          { label: 'String', value: 'String' },
                                                                                          { label: 'Number', value: 'Number' },
                                                                                          { label: 'Boolean', value: 'Boolean' },
                                                                                          { label: 'Integer', value: 'Integer' },
                                                                                          { label: 'Float', value: 'Float' },
                                                                                        ]}
                                                                                      />
                                                                                    </Form.Item>
                                                                                  </Col>
                                                                                  <Col span={4}>
                                                                                    <Form.Item label="Read/Write">
                                                                                      <Select
                                                                                        placeholder="Select"
                                                                                        value={tag.read_write || null}
                                                                                        onChange={(val) => {
                                                                                          setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].read_write`, val);
                                                                                          if (val !== 'set') setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].value`, '');
                                                                                        }}
                                                                                        options={[
                                                                                          { label: 'START', value: 'start' },
                                                                                          { label: 'STOP', value: 'stop' },
                                                                                          { label: 'READ', value: 'read' },
                                                                                          { label: 'SET', value: 'set' },
                                                                                        ]}
                                                                                      />
                                                                                    </Form.Item>
                                                                                  </Col>
                                                                                  {tag.read_write === 'set' && (
                                                                                    <Col span={4}>
                                                                                      <Form.Item label="Value">
                                                                                        {tag.data_type === 'Boolean' ? (
                                                                                          <Select
                                                                                            placeholder="Select Value"
                                                                                            value={tag.value || null}
                                                                                            onChange={(val) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].value`, val)}
                                                                                            options={[{ label: 'True', value: 'True' }, { label: 'False', value: 'False' }]}
                                                                                          />
                                                                                        ) : (
                                                                                          <Input
                                                                                            placeholder="Enter Value"
                                                                                            value={tag.value}
                                                                                            onChange={(e) => setFieldValue(`${rfBase}.additional_fields.buttons[${bi}].tags[${tagIndex}].value`, e.target.value)}
                                                                                          />
                                                                                        )}
                                                                                      </Form.Item>
                                                                                    </Col>
                                                                                  )}
                                                                                  <Col span={tag.read_write === 'set' ? 1 : 5} className="flex items-end">
                                                                                    {(btn.tags || []).length > 1 && (
                                                                                      <Button danger size="small" onClick={() => removeRfTag(tagIndex)} icon={<MinusCircleOutlined />} className="mb-6" />
                                                                                    )}
                                                                                  </Col>
                                                                                </Row>
                                                                              </div>
                                                                            );
                                                                          })}
                                                                        </div>
                                                                      )}
                                                                    </FieldArray>
                                                                  </div>
                                                                </div>
                                                              ))}
                                                              <Button type="dashed" onClick={() => pushBtn({ label: '', color: '#1677ff', hidden: false, auto: false, tags: [{ tag_name: null, data_type: '', read_write: null, value: '', machine_id: null, machine_group_id: null, parameter_name: null }] })} icon={<PlusOutlined />} className="w-full">
                                                                Add Button
                                                              </Button>
                                                            </div>
                                                          )}
                                                        </FieldArray>
                                                      </Form.Item>
                                                    </Col>
                                                  )}
                                                </Row>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </FieldArray>
                                  </div>
                                </Col>
                              );
                            })()}

                            {/* Buttons Section */}
                            {field.type === 'button' && (
                              <Col span={24}>
                                <Form.Item label="Buttons">
                                  <FieldArray
                                    name={`fields[${fieldIndex}].additional_fields.buttons`}
                                  >
                                    {({ push, remove }) => (
                                      <div className="flex flex-col gap-3">
                                        <div
                                          className="flex flex-col gap-3 overflow-y-auto"
                                          style={{
                                            maxHeight:
                                              field.additional_fields?.buttons?.length > 2
                                                ? '400px'
                                                : 'none',
                                            paddingRight:
                                              field.additional_fields?.buttons?.length > 2
                                                ? '8px'
                                                : '0',
                                          }}
                                        >
                                          {field.additional_fields?.buttons?.map(
                                            (button, buttonIndex) => (
                                              <div
                                                key={buttonIndex}
                                                className="border border-gray-200 rounded-lg bg-white overflow-hidden"
                                              >
                                                {/* Preview strip + config row */}
                                                <div className="flex items-stretch">
                                                  {/* Live preview */}
                                                  <div
                                                    className="flex flex-col items-center justify-center gap-2 px-5 py-3 flex-shrink-0 min-w-[110px] border-r border-gray-200"
                                                    style={{ backgroundColor: (button.color || '#1677ff') + '15' }}
                                                  >
                                                    <div
                                                      className="px-3 py-1.5 rounded-md text-white text-sm font-semibold text-center w-full"
                                                      style={{ backgroundColor: button.color || '#1677ff' }}
                                                    >
                                                      {button.label || 'Button'}
                                                    </div>
                                                    <div className="flex flex-wrap gap-1 justify-center">
                                                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${button.hidden ? 'bg-gray-300 text-gray-700' : 'bg-gray-100 text-gray-400'}`}>
                                                        {button.hidden ? 'Hidden' : 'Visible'}
                                                      </span>
                                                      {button.auto && (
                                                        <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700">Auto</span>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Config fields */}
                                                  <div className="flex-1 p-3">
                                                    <Row gutter={[12, 8]} align="middle">
                                                      <Col span={8}>
                                                        <Form.Item label="Label" style={{ marginBottom: 0 }}
                                                          validateStatus={touched.fields?.[fieldIndex]?.additional_fields?.buttons?.[buttonIndex]?.label && errors.fields?.[fieldIndex]?.additional_fields?.buttons?.[buttonIndex]?.label ? 'error' : ''}
                                                          help={touched.fields?.[fieldIndex]?.additional_fields?.buttons?.[buttonIndex]?.label && errors.fields?.[fieldIndex]?.additional_fields?.buttons?.[buttonIndex]?.label}
                                                        >
                                                          <Input
                                                            placeholder="Button Label"
                                                            name={`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].label`}
                                                            value={button.label}
                                                            onChange={handleChange}
                                                          />
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={9}>
                                                        <Form.Item label="Color" style={{ marginBottom: 0 }}>
                                                          <Space.Compact className="w-full">
                                                            <ColorPicker
                                                              value={button.color || '#1677ff'}
                                                              onChange={(color) => setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].color`, color.toHexString())}
                                                            />
                                                            <Input
                                                              value={button.color || ''}
                                                              placeholder="#1677ff"
                                                              onChange={(e) => setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].color`, e.target.value)}
                                                            />
                                                          </Space.Compact>
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={3}>
                                                        <Form.Item label="Hidden" style={{ marginBottom: 0 }}>
                                                          <Switch
                                                            checked={button.hidden || false}
                                                            onChange={(v) => setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].hidden`, v)}
                                                            checkedChildren="Yes" unCheckedChildren="No"
                                                          />
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={3}>
                                                        <Form.Item label="Auto" style={{ marginBottom: 0 }}>
                                                          <Switch
                                                            checked={button.auto || false}
                                                            onChange={(v) => setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].auto`, v)}
                                                            checkedChildren="Yes" unCheckedChildren="No"
                                                          />
                                                        </Form.Item>
                                                      </Col>
                                                    </Row>
                                                  </div>

                                                  {/* Remove */}
                                                  <div className="flex items-center px-2 border-l border-gray-100">
                                                    <Button danger type="text" onClick={() => remove(buttonIndex)} icon={<MinusCircleOutlined />} />
                                                  </div>
                                                </div>

                                                {/* Tags Section */}
                                                <div className="pl-4 border-l-2 border-blue-300">
                                                  <p className="text-xs font-semibold text-gray-600 mb-2">
                                                    Tags
                                                  </p>
                                                  <FieldArray
                                                    name={`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags`}
                                                  >
                                                    {({ push: pushTag, remove: removeTag }) => (
                                                      <div className="flex flex-col gap-2">
                                                        <div
                                                          className="flex flex-col gap-2 overflow-y-auto"
                                                          style={{
                                                            maxHeight:
                                                              button.tags?.length > 1
                                                                ? '300px'
                                                                : 'none',
                                                            paddingRight:
                                                              button.tags?.length > 1 ? '8px' : '0',
                                                          }}
                                                        >
                                                          {button.tags?.map((tag, tagIndex) => {
                                                            const tagKey = `${fieldIndex}-${buttonIndex}-${tagIndex}`;
                                                            return (
                                                              <div
                                                                key={tagIndex}
                                                                className="p-3 border border-blue-200 rounded-md bg-blue-50"
                                                              >
                                                                <Row
                                                                  gutter={[12, 12]}
                                                                  align="middle"
                                                                >
                                                                                  {/* Machine Type + Group Cascader */}
                                                                  <Col span={6}>
                                                                    <Form.Item label="Machine Type">
                                                                      <Cascader
                                                                        placeholder="Select Machine Type"
                                                                        showSearch
                                                                        allowClear
                                                                        style={{ width: '100%' }}
                                                                        popupClassName="cascader-wide-dropdown"
                                                                        dropdownMenuColumnStyle={{ minWidth: 200 }}
                                                                        value={selectedCascaderPerTag[tagKey] || undefined}
                                                                        onChange={(val, selectedOptions) => {
                                                                          setSelectedCascaderPerTag((prev) => ({ ...prev, [tagKey]: val || null }));
                                                                          const machineTypeId = val?.[0] || null;
                                                                          const machineGroupId = val?.[1] || null;
                                                                          setSelectedMachinePerTag((prev) => ({ ...prev, [tagKey]: machineGroupId || machineTypeId }));
                                                                          setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].machine_id`, machineTypeId);
                                                                          setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].machine_group_id`, machineGroupId);
                                                                          setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].tag_name`, null);
                                                                          setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].parameter_name`, null);
                                                                          setFieldValue(`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].data_type`, null);
                                                                          fetchTagsForMachine(machineGroupId || machineTypeId, tagKey);
                                                                        }}
                                                                        options={machineCascaderOptions}
                                                                      />
                                                                    </Form.Item>
                                                                  </Col>

                                                                  {/* Tag Name */}
                                                                  <Col span={5}>
                                                                    <Form.Item
                                                                      label="Parameter Name"
                                                                      validateStatus={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.tag_name &&
                                                                          errors.fields?.[fieldIndex]
                                                                            ?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.tag_name
                                                                          ? 'error'
                                                                          : ''
                                                                      }
                                                                      help={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.tag_name &&
                                                                        errors.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.tag_name
                                                                      }
                                                                    >
                                                                      <Select
                                                                        placeholder="Select Parameter"
                                                                        value={
                                                                          tag.parameter_name || null
                                                                        }
                                                                        disabled={
                                                                          !selectedMachinePerTag[
                                                                          tagKey
                                                                          ]
                                                                        }
                                                                        onChange={(val) => {
                                                                          setFieldValue(
                                                                            `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].parameter_name`,
                                                                            val || null
                                                                          );
                                                                          const tagsList =
                                                                            tagsListPerMachine[
                                                                            tagKey
                                                                            ] || [];
                                                                          const selectedParam =
                                                                            tagsList?.find(
                                                                              (p) =>
                                                                                p.parameter_name ===
                                                                                val
                                                                            );
                                                                          if (selectedParam) {
                                                                            const dtList = Array.isArray(selectedParam.data_type) ? selectedParam.data_type : (selectedParam.data_type ? [selectedParam.data_type] : []);
                                                                            setFieldValue(
                                                                              `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].data_type`,
                                                                              dtList.length === 1 ? dtList[0] : (dtList[0] || '')
                                                                            );
                                                                            setFieldValue(
                                                                              `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].tag_name`,
                                                                              val
                                                                            );
                                                                          } else {
                                                                            // cleared or not found — reset data_type and tag_name
                                                                            setFieldValue(
                                                                              `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].data_type`,
                                                                              ''
                                                                            );
                                                                            setFieldValue(
                                                                              `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].tag_name`,
                                                                              null
                                                                            );
                                                                          }
                                                                        }}
                                                                        showSearch
                                                                        allowClear
                                                                        optionFilterProp="children"
                                                                        filterOption={(
                                                                          input,
                                                                          option
                                                                        ) =>
                                                                          (option?.label ?? '')
                                                                            .toLowerCase()
                                                                            .includes(
                                                                              input.toLowerCase()
                                                                            )
                                                                        }
                                                                        options={(
                                                                          tagsListPerMachine[
                                                                          tagKey
                                                                          ] || []
                                                                        )
                                                                          ?.filter((p) => {
                                                                            const selectedParamNames =
                                                                              button.tags
                                                                                .map((t, idx) =>
                                                                                  idx !== tagIndex
                                                                                    ? t.parameter_name
                                                                                    : null
                                                                                )
                                                                                .filter(Boolean);
                                                                            return !selectedParamNames.includes(
                                                                              p.parameter_name
                                                                            );
                                                                          })
                                                                          .map((p) => ({
                                                                            value: p.parameter_name,
                                                                            label: p.parameter_name,
                                                                          }))}
                                                                      />
                                                                    </Form.Item>
                                                                  </Col>

                                                                  {/* Data Type */}
                                                                  <Col span={4}>
                                                                    <Form.Item
                                                                      label="Data Type"
                                                                      validateStatus={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.data_type &&
                                                                          errors.fields?.[fieldIndex]
                                                                            ?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.data_type
                                                                          ? 'error'
                                                                          : ''
                                                                      }
                                                                      help={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.data_type &&
                                                                        errors.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.data_type
                                                                      }
                                                                    >
                                                                      <Select
                                                                        placeholder="Auto-filled"
                                                                        value={
                                                                          tag.data_type || null
                                                                        }
                                                                        onChange={(val) =>
                                                                          setFieldValue(
                                                                            `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].data_type`,
                                                                            val
                                                                          )
                                                                        }
                                                                        disabled={!!tag.parameter_name}
                                                                        options={[
                                                                          {
                                                                            label: 'String',
                                                                            value: 'String',
                                                                          },
                                                                          {
                                                                            label: 'Number',
                                                                            value: 'Number',
                                                                          },
                                                                          {
                                                                            label: 'Boolean',
                                                                            value: 'Boolean',
                                                                          },
                                                                          {
                                                                            label: 'Integer',
                                                                            value: 'Integer',
                                                                          },
                                                                          {
                                                                            label: 'Float',
                                                                            value: 'Float',
                                                                          },
                                                                        ]}
                                                                      />
                                                                    </Form.Item>
                                                                  </Col>

                                                                  {/* Read/Write */}
                                                                  <Col span={4}>
                                                                    <Form.Item
                                                                      label="Read/Write"
                                                                      validateStatus={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.read_write &&
                                                                          errors.fields?.[fieldIndex]
                                                                            ?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.read_write
                                                                          ? 'error'
                                                                          : ''
                                                                      }
                                                                      help={
                                                                        touched.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.read_write &&
                                                                        errors.fields?.[fieldIndex]
                                                                          ?.additional_fields
                                                                          ?.buttons?.[buttonIndex]
                                                                          ?.tags?.[tagIndex]
                                                                          ?.read_write
                                                                      }
                                                                    >
                                                                      <Select
                                                                        placeholder="Select"
                                                                        value={
                                                                          tag.read_write || null
                                                                        }
                                                                        onChange={(val) => {
                                                                          setFieldValue(
                                                                            `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].read_write`,
                                                                            val
                                                                          );
                                                                          if (val !== 'set') {
                                                                            setFieldValue(
                                                                              `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].value`,
                                                                              ''
                                                                            );
                                                                          }
                                                                        }}
                                                                        options={[
                                                                          { label: 'START', value: 'start' },
                                                                          { label: 'STOP', value: 'stop' },
                                                                          { label: 'READ', value: 'read' },
                                                                          { label: 'SET', value: 'set' },
                                                                        ]}
                                                                      />
                                                                    </Form.Item>
                                                                  </Col>

                                                                  {/* Value (only for write mode) */}
                                                                  {tag.read_write === 'set' && (
                                                                    <Col span={4}>
                                                                      <Form.Item
                                                                        label="Value"
                                                                        validateStatus={
                                                                          touched.fields?.[
                                                                            fieldIndex
                                                                          ]?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.value &&
                                                                            errors.fields?.[
                                                                              fieldIndex
                                                                            ]?.additional_fields
                                                                              ?.buttons?.[buttonIndex]
                                                                              ?.tags?.[tagIndex]
                                                                              ?.value
                                                                            ? 'error'
                                                                            : ''
                                                                        }
                                                                        help={
                                                                          touched.fields?.[
                                                                            fieldIndex
                                                                          ]?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.value &&
                                                                          errors.fields?.[
                                                                            fieldIndex
                                                                          ]?.additional_fields
                                                                            ?.buttons?.[buttonIndex]
                                                                            ?.tags?.[tagIndex]
                                                                            ?.value
                                                                        }
                                                                      >
                                                                        {tag.data_type ===
                                                                          'Boolean' ? (
                                                                          <Select
                                                                            placeholder="Select Value"
                                                                            value={
                                                                              tag.value || null
                                                                            }
                                                                            onChange={(val) =>
                                                                              setFieldValue(
                                                                                `fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].value`,
                                                                                val
                                                                              )
                                                                            }
                                                                            options={[
                                                                              {
                                                                                label: 'True',
                                                                                value: 'True',
                                                                              },
                                                                              {
                                                                                label: 'False',
                                                                                value: 'False',
                                                                              },
                                                                            ]}
                                                                          />
                                                                        ) : (
                                                                          <Input
                                                                            placeholder="Enter Value"
                                                                            name={`fields[${fieldIndex}].additional_fields.buttons[${buttonIndex}].tags[${tagIndex}].value`}
                                                                            value={tag.value}
                                                                            onChange={handleChange}
                                                                          />
                                                                        )}
                                                                      </Form.Item>
                                                                    </Col>
                                                                  )}

                                                                  {/* Remove Tag Button */}
                                                                  <Col
                                                                    span={
                                                                      tag.read_write === 'set'
                                                                        ? 2
                                                                        : 6
                                                                    }
                                                                  >
                                                                    {button.tags?.length > 1 && (
                                                                      <Button
                                                                        danger
                                                                        size="small"
                                                                        onClick={() =>
                                                                          removeTag(tagIndex)
                                                                        }
                                                                        icon={
                                                                          <MinusCircleOutlined />
                                                                        }
                                                                        className="mt-6"
                                                                      />
                                                                    )}
                                                                  </Col>
                                                                </Row>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>

                                                        <Button
                                                          type="dashed"
                                                          size="small"
                                                          onClick={() =>
                                                            pushTag({
                                                              tag_name: null,
                                                              machine_id: null,
                                                              data_type: '',
                                                              value: '',
                                                              read_write: 'read',
                                                            })
                                                          }
                                                          icon={<PlusOutlined />}
                                                          className="w-full"
                                                        >
                                                          Add Tag
                                                        </Button>
                                                      </div>
                                                    )}
                                                  </FieldArray>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>

                                        <Button
                                          type="dashed"
                                          onClick={() =>
                                            push({
                                              label: '',
                                              color: '#1677ff',
                                              hidden: false,
                                              auto: false,
                                              tags: [
                                                {
                                                  tag_name: null,
                                                  machine_id: null,
                                                  data_type: '',
                                                  value: '',
                                                  read_write: 'read',
                                                },
                                              ],
                                            })
                                          }
                                          icon={<PlusOutlined />}
                                          className="w-full"
                                        >
                                          Add Button
                                        </Button>
                                      </div>
                                    )}
                                  </FieldArray>
                                </Form.Item>
                              </Col>
                            )}
                          </Row>
                        </div>
                      ))}

                      {/* Add Field Button — maps to existing add-step handler */}
                      <Button
                        type="dashed"
                        onClick={() => pushField({ ...emptyField })}
                        icon={<PlusOutlined />}
                        className="w-full"
                      >
                        Add Action Step
                      </Button>
                    </div>
                  )}
                    </FieldArray>
                  </Col>
                  </Row>
                </div>
                </div>{/* end scrollable form area */}
              </div>

              {/* ===== RIGHT: Summary + Flow Panels — only rendered when rulesEnabled ===== */}
              {rulesEnabled && (
                <div className="w-1/4 min-w-0 flex-shrink-0">
                  {/* ── Execution Summary ── */}
                  <div
                    className="rounded-lg border w-full mb-4"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '14px 16px' }}
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      Execution Summary
                    </p>
                    <div className="flex flex-col gap-1">
                      {[
                        { label: 'Total Action Steps', value: totalSteps },
                        { label: 'Mandatory Steps', value: mandatorySteps },
                        { label: 'Blocking Steps', value: blockingSteps },
                        { label: 'E-Sign Required', value: eSignRequired },
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between py-1">
                          <span className="text-sm font-medium text-gray-500">{row.label}</span>
                          <span className="text-sm font-semibold text-gray-800">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Runtime Execution Flow ── */}
                  <div
                    className="rounded-lg border w-full"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff', padding: '14px 16px' }}
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                      Runtime Execution Flow
                    </p>
                    <div className="flex flex-col gap-2">
                      {executionFlowSteps.map((step, idx) => (
                        <div key={step} className="flex items-center gap-2">
                          <Badge
                            count={idx + 1}
                            style={{ backgroundColor: '#3b82f6', flexShrink: 0 }}
                          />
                          <span className="text-sm text-gray-600">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Full-width footer — Save spans the whole card */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 }}>
              <Button type="primary" htmlType="submit" block>
                {editIndex !== null ? 'Update' : 'Save Action List'}
              </Button>
            </div>
          </Form>
          );
        }}
      </Formik>
      <Modal title="Enter Your Field" open={isModalOpen} onCancel={handleCancel} footer={null}>
        <div className="space-y-4">
          <Input
            placeholder="Enter value..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="rounded-md border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />

          <div className="flex justify-end">
            <Button
              type="primary"
              className="bg-blue-500 hover:bg-blue-600"
              onClick={handleOk}
              disabled={!value?.trim()}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ActionList;

export const ActionListCard = ({ fromView, item, onEdit, onDelete }) => {
  const { name, fields, notifications } = item;
  const notifTargetLabel = (v) => NOTIFY_TARGETS.find((t) => t.value === v)?.label || v;
  const notifCriteriaLabel = (v) => NOTIFY_CRITERIA.find((c) => c.value === v)?.label || v;
  const notifActionLabel = (v) => NOTIFY_ACTIONS.find((a) => a.value === v)?.label || v;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition p-3 text-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-100 rounded-md">
            <Package className="w-4 h-4 text-indigo-600" />
          </div>
          <p
            className="font-semibold text-gray-900 leading-tight truncate text-base w-64"
            title={name}
          >
            {name}
          </p>
        </div>

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit action"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete action"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fields */}
      {Array.isArray(fields) &&
        fields.map((field, fieldIdx) => (
          <div key={fieldIdx} className="mt-2 border border-gray-100 rounded-md p-2.5">
            {/* Field header row */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-500">Field {fieldIdx + 1}</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-medium">
                {field.type}
              </span>
              {field.name && (
                <span className="text-xs font-medium text-gray-700">{field.name}</span>
              )}
              {field.additional_fields?.required && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium ml-auto">
                  Required
                </span>
              )}
            </div>

            {/* Options (dropdown / radio) */}
            {(field.type === 'dropdown' || field.type === 'radio') &&
              field.additional_fields?.options?.length > 0 && (
                <div className="bg-orange-50 rounded-md p-2 mb-2">
                  <p className="text-xs text-orange-700 font-medium mb-1 flex items-center gap-1">
                    Options <ChevronDown className="w-3 h-3" />
                  </p>
                  <ul className="text-orange-900 text-xs list-disc list-inside space-y-0.5">
                    {field.additional_fields.options.map((opt, idx) => (
                      <li key={idx}>
                        {opt.label} <span className="text-gray-500">({opt.value})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Buttons (button type) */}
            {field.type === 'button' && field.additional_fields?.buttons?.length > 0 && (
              <div className="bg-purple-50 rounded-md p-2">
                <p className="text-xs text-purple-700 font-medium mb-2">
                  Buttons ({field.additional_fields.buttons.length})
                </p>
                <div className="overflow-x-auto pb-1">
                  <div className="flex gap-2" style={{ minWidth: 'fit-content' }}>
                    {field.additional_fields.buttons.map((btn, idx) => (
                      <div
                        key={idx}
                        className="inline-flex flex-col gap-1.5 px-3 py-2 rounded-md border flex-shrink-0"
                        style={{
                          backgroundColor: btn.color + '20',
                          borderColor: btn.color,
                        }}
                      >
                        {/* Button label + color dot */}
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: btn.color }}
                          />
                          <span className="font-medium text-sm" style={{ color: btn.color }}>
                            {btn.label}
                          </span>
                        </div>

                        {/* Hidden / Auto badges */}
                        <div className="flex items-center gap-1">
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded font-medium ${btn.hidden ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-400'
                              }`}
                          >
                            Hidden: {btn.hidden ? 'Yes' : 'No'}
                          </span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded font-medium ${btn.auto ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-400'
                              }`}
                          >
                            Auto: {btn.auto ? 'Yes' : 'No'}
                          </span>
                        </div>

                        {/* Tags */}
                        {btn.tags && btn.tags.length > 0 && (
                          <div className="flex flex-col gap-1 mt-1">
                            {btn.tags.map((tag, tagIdx) => (
                              <div
                                key={tagIdx}
                                className="flex flex-col gap-0.5 text-xs bg-white bg-opacity-70 px-2 py-1.5 rounded border border-gray-100"
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-semibold text-gray-800">
                                    {tag.parameter_name || tag.tag_name || '—'}
                                  </span>
                                  {tag.data_type && (
                                    <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                      {tag.data_type}
                                    </span>
                                  )}
                                  <span className={`ml-auto px-1.5 py-0.5 rounded font-medium uppercase
                                    ${tag.read_write === 'start' ? 'bg-green-100 text-green-700' :
                                      tag.read_write === 'stop' ? 'bg-red-100 text-red-700' :
                                      tag.read_write === 'set' ? 'bg-orange-100 text-orange-700' :
                                      'bg-gray-100 text-gray-700'}`}>
                                    {tag.read_write}
                                  </span>
                                </div>
                                {tag.read_write === 'set' && tag.value && (
                                  <div className="text-gray-500">
                                    Value: <span className="text-gray-800 font-medium">{tag.value}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

      {/* Notifications summary */}
      {Array.isArray(notifications) && notifications.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <span className="text-xs font-semibold text-gray-500">Notifications</span>
          <div className="flex flex-col gap-1.5 mt-1.5">
            {notifications.map((n, i) => {
              const src = n.condition_source || 'none';
              const srcLabel = CONDITION_SOURCES.find((c) => c.value === src)?.label || src;
              const conditionSummary =
                src === 'duration'
                  ? `${srcLabel} — ${n.duration_operator || ''} ${n.duration_value ?? ''}${n.duration_unit ? ` ${n.duration_unit}` : ''}`
                  : src === 'ingredient'
                  ? `${srcLabel} ${n.threshold_operator || ''} ${n.threshold_value ?? ''}${n.ingredient_unit ? ` ${n.ingredient_unit}` : ''}`
                  : src === 'monitoring'
                  ? `${srcLabel} ${n.threshold_operator || ''} ${n.threshold_value ?? ''}${n.monitoring_unit ? ` ${n.monitoring_unit}` : ''}`
                  : src === 'sampling'
                  ? `${srcLabel} — Expected: ${n.sampling_expected || 'pass'}`
                  : src === 'prior_action'
                  ? `${srcLabel} — Response: ${n.prior_action_expected || 'yes'}`
                  : srcLabel;
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 flex-wrap text-xs bg-gray-50 px-2 py-1.5 rounded border border-gray-100"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      n.severity === 'alert'
                        ? 'bg-red-500'
                        : n.severity === 'notification'
                        ? 'bg-orange-400'
                        : 'bg-blue-400'
                    }`}
                  />
                  <span className="font-medium text-gray-800">{n.name || `Notification ${i + 1}`}</span>
                  <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs">
                    {conditionSummary}
                  </span>
                  <span className="text-gray-500">{notifTargetLabel(n.target)}</span>
                  <span className="ml-auto px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                    {notifActionLabel(n.action)}
                  </span>
                  {!n.enabled && (
                    <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">Off</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
